import type { ApolloLink } from '@apollo/client';
import { ApolloProvider, createHttpLink, split } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { RetryLink } from '@apollo/client/link/retry';
import { createUploadLink } from 'apollo-upload-client';
import type { ReactNode } from 'react';
import React, { useMemo } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import { config } from 'src/config';
import { createClient } from 'src/shared/graphql/client';
import { graphqlFetch } from 'src/shared/graphql/client/link/utils/graphqlFetch';
import { loginPathWithRedirectTo } from 'src/shared/helpers/routing';
import { useClosure } from 'src/shared/hooks';
import { LOGIN_REDIRECT_EXCLUSIONS } from 'src/shared/route';

interface ApolloClientProps {
  children: ReactNode;
}

const BatchOperations = [
  'AiGeneratedImage',
  'CreateAiGeneratedImageAsync',
  'GenerateAiImage',
];

const ApolloClientProvider = ({ children }: ApolloClientProps) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const match = useRouteMatch(LOGIN_REDIRECT_EXCLUSIONS);

  const onUnauthenticated = useClosure((skipRedirect: boolean) => {
    if (!match && !skipRedirect) {
      history.push(loginPathWithRedirectTo(pathname));
    }
  });

  // Memoize the apollo client to prevent recreating it
  // every time the provider re-renders. For example, when
  // the client routes to different pages.
  const client = useMemo(() => {
    const options = {
      uri: config.GRAPHQL_URI,
      credentials: 'include',
      // eslint-disable-next-line no-restricted-globals
      fetch: graphqlFetch as typeof fetch,
    } as const;

    const batchTest = ({ operationName }: { operationName: string }) =>
      !config.GRAPHQL_DISABLE_BATCHING &&
      BatchOperations.includes(operationName);
    const uploadTest = ({ operationName }: { operationName: string }) =>
      operationName === 'UploadAiOriginalImage';
    const batchTerminator = new BatchHttpLink(options);

    // File uploads are not supported in the federated graph so
    // use the direct graphql endpoint for uploads
    const uploadTerminator = createUploadLink({
      ...options,
      uri: config.GRAPHQL_DIRECT_URI,
    });
    const defaultTerminator = createHttpLink(options);

    const terminator = split(
      uploadTest,
      uploadTerminator as unknown as ApolloLink,
      defaultTerminator,
    );

    const retryLink = new RetryLink({
      delay: {
        initial: 100,
        max: Infinity,
        jitter: true,
      },
      attempts: {
        max: 3,
      },
    });

    return createClient({
      state: window.initialState,
      onUnauthenticated,
      terminatingLink: split(batchTest, batchTerminator, terminator),
      links: [retryLink],
    });
  }, [onUnauthenticated]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export { ApolloClientProvider };
